import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Award, Radio, Loader2, ImageIcon, Download, ArrowRight, AlertCircle, RefreshCw } from "lucide-react";
import api, { API } from "@/lib/api";
import PublicNav from "@/components/PublicNav";
import { Button } from "@/components/ui/button";

function DiplomaCard({ d }) {
  return (
    <article
      data-testid={`public-diploma-card-${d.id}`}
      className="rounded-md border border-zinc-400 bg-white overflow-hidden flex flex-col"
    >
      <div className="aspect-[3/2] bg-zinc-100 overflow-hidden flex items-center justify-center">
        {d.has_template ? (
          <img
            src={`${API}/diplomas/${d.id}/template?t=${d.id}`}
            alt={d.name}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="text-center text-zinc-500">
            <ImageIcon className="h-10 w-10 mx-auto mb-1" />
            <span className="text-xs uppercase tracking-widest">Sin plantilla</span>
          </div>
        )}
      </div>
      <div className="p-5 flex-1 flex flex-col">
        <div className="flex items-center justify-between mb-1">
          <span className="text-[10px] uppercase tracking-[0.25em] text-[#5EA529] font-semibold">
            {d.type === "qsl" ? "QSL" : "Diploma"}
            {d.station_callsign ? ` · ${d.station_callsign}` : ""}
          </span>
          {d.required_contacts > 1 && (
            <span className="text-[10px] font-mono text-zinc-600">
              {d.required_contacts} contactos
            </span>
          )}
        </div>
        <h3 className="font-mono font-bold text-xl tracking-tight text-black leading-tight">
          {d.name}
        </h3>
        {d.subtitle && (
          <p className="text-sm text-zinc-700 italic mt-1">{d.subtitle}</p>
        )}
        {(d.required_band || d.required_mode) && (
          <p className="text-xs font-mono text-zinc-600 mt-2">
            Requisito:
            {d.required_band ? ` ${d.required_band.toUpperCase()}` : ""}
            {d.required_mode ? ` · ${d.required_mode}` : ""}
          </p>
        )}
        {d.bases && (
          <div
            data-testid={`public-diploma-bases-${d.id}`}
            className="mt-4 pt-4 border-t border-zinc-200 text-sm text-zinc-800 whitespace-pre-wrap leading-relaxed flex-1"
          >
            {d.bases}
          </div>
        )}
      </div>
    </article>
  );
}

function EmptyState({ label }) {
  return (
    <div className="col-span-full py-10 text-center text-zinc-600 font-mono text-sm">
      {label}
    </div>
  );
}

export default function DiplomasActivos() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    setError(false);
    api
      .get("/diplomas")
      .then(({ data }) => {
        setItems(Array.isArray(data) ? data : []);
        setError(false);
      })
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const diplomas = items.filter((d) => d.type !== "qsl" && d.type !== "activator");
  const qsls = items.filter((d) => d.type === "qsl");

  return (
    <div className="min-h-screen bg-[#D6D3D1] text-zinc-900">
      <PublicNav />
      <main className="max-w-5xl mx-auto px-5 py-10">
        <section className="mb-10">
          <p className="text-xs uppercase tracking-[0.3em] text-black/60 font-semibold mb-2">
            Radio Club Ochentero · Premios en juego
          </p>
          <h1 className="font-mono text-3xl sm:text-4xl font-bold tracking-tighter text-black leading-none inline-block border-b-[8px] border-[#5EA529] pb-1">
            Diplomas y QSL activos
          </h1>
          <p className="mt-4 max-w-2xl text-sm text-zinc-700 leading-relaxed">
            Bienvenido/a. Aquí puedes consultar los diplomas y tarjetas QSL que el club
            tiene abiertos ahora mismo, junto con las bases de cada uno. Cuando tengas los
            contactos necesarios, ve a{" "}
            <Link
              to="/descarga"
              className="text-[#5EA529] font-semibold hover:underline inline-flex items-center gap-1"
            >
              Descarga tu diploma/QSL <ArrowRight className="h-3.5 w-3.5" />
            </Link>{" "}
            e introduce tu indicativo para verificarlos y descargarlo.
          </p>
        </section>

        {loading ? (
          <div className="py-20 flex flex-col items-center gap-3">
            <Loader2 className="h-8 w-8 animate-spin text-[#5EA529]" />
            <p className="text-xs font-mono uppercase tracking-widest text-zinc-700">
              Cargando diplomas…
            </p>
          </div>
        ) : error ? (
          <div
            data-testid="diplomas-error-state"
            className="rounded-md border border-amber-400 bg-amber-50 p-6 max-w-xl mx-auto"
          >
            <div className="flex items-start gap-3">
              <AlertCircle className="h-6 w-6 text-amber-600 shrink-0 mt-0.5" />
              <div className="flex-1">
                <h3 className="font-mono font-bold text-amber-900">
                  Servidor no disponible temporalmente
                </h3>
                <p className="text-sm text-amber-800 mt-2 leading-relaxed">
                  El servidor puede estar dormido (entorno de vista previa). Prueba lo
                  siguiente en orden:
                </p>
                <ol className="text-sm text-amber-900 mt-2 ml-5 list-decimal space-y-1">
                  <li>
                    Si ves una barra abajo con el botón{" "}
                    <strong className="font-mono bg-amber-200 px-1.5 py-0.5 rounded">
                      Wake up servers
                    </strong>
                    , púlsalo y espera 20-30 segundos.
                  </li>
                  <li>Después pulsa <strong>Reintentar</strong> aquí.</li>
                  <li>Si sigue sin funcionar, recarga la página con Ctrl+Shift+R.</li>
                </ol>
                <Button
                  onClick={load}
                  data-testid="diplomas-retry-btn"
                  className="mt-4 bg-amber-600 hover:bg-amber-700 text-white rounded-sm"
                >
                  <RefreshCw className="h-4 w-4 mr-2" /> Reintentar
                </Button>
              </div>
            </div>
          </div>
        ) : (
          <>
            {items.length === 0 && (
              <p
                data-testid="no-active-message"
                className="text-center text-zinc-600 py-10"
              >
                No hay diplomas ni QSL activos en este momento. Vuelve pronto.
              </p>
            )}

            {diplomas.length > 0 && (
              <section className="mb-12" data-testid="section-diplomas">
                <div className="flex items-center gap-2 mb-4">
                  <Award className="h-5 w-5 text-[#5EA529]" />
                  <h2 className="font-mono text-lg uppercase tracking-[0.25em] font-bold text-black">
                    Diplomas
                  </h2>
                  <span className="text-xs font-mono text-zinc-600">
                    ({diplomas.length})
                  </span>
                </div>
                <div className="grid md:grid-cols-2 gap-5">
                  {diplomas.map((d) => (
                    <DiplomaCard key={d.id} d={d} />
                  ))}
                </div>
              </section>
            )}

            {qsls.length > 0 && (
              <section className="mb-12" data-testid="section-qsls">
                <div className="flex items-center gap-2 mb-4">
                  <Radio className="h-5 w-5 text-[#5EA529]" />
                  <h2 className="font-mono text-lg uppercase tracking-[0.25em] font-bold text-black">
                    Tarjetas QSL
                  </h2>
                  <span className="text-xs font-mono text-zinc-600">({qsls.length})</span>
                </div>
                <div className="grid md:grid-cols-2 gap-5">
                  {qsls.map((d) => (
                    <DiplomaCard key={d.id} d={d} />
                  ))}
                </div>
              </section>
            )}

            <div className="mt-8 text-center">
              <Link to="/descarga">
                <Button
                  data-testid="cta-go-descarga"
                  className="bg-[#5EA529] hover:bg-[#4f8c22] text-white font-semibold rounded-sm h-12 px-8"
                >
                  <Download className="h-4 w-4 mr-2" /> Comprobar mi indicativo
                </Button>
              </Link>
            </div>
          </>
        )}
      </main>
    </div>
  );
}
